import React, { Suspense, useMemo, useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useLazyLoadQuery } from 'react-relay/hooks';

import { createStyles, ListItemText, Paper, Theme, WithStyles, withStyles } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import { ErrorBoundary } from 'core';

import { useQueryOptions } from '@lib/hooks';

import {
  DialogMessageTemplateAuditRestore,
  ListAuditsMessageTemplate,
  TypographyMessageTemplateKey,
} from 'deprecated/fragments';

import { Button, ListItem, ToolbarNavigation, Typography } from 'components/v2';
import { Audit } from 'deprecated/fragments/types/auditType';
import { QueryAuditsMessageTemplateQuery } from './__generated__/QueryAuditsMessageTemplateQuery.graphql';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    gap: {
      flex: 1,
    },
    body: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
      fontSize: 11,
    },
    content: {
      width: '100%',
      overflowX: 'auto',
      marginRight: 0,
    },
    listWrapper: {
      width: 268,
      minWidth: 268,
      marginLeft: unit * 2,
      overflow: 'auto',
    },
  });

const messages = defineMessages({
  title: {
    id: 'deprecated.queries.QueryAuditsMessageTemplate.title',
    defaultMessage: 'Version History for {value}',
  },
  actionRestoreVersion: {
    id: 'deprecated.queries.QueryAuditsMessageTemplate.actionRestoreVersion',
    defaultMessage: 'Use this version',
  },
  initialTemplatePrimary: {
    id: 'deprecated.queries.QueryAuditsMessageTemplate.initialTemplatePrimary',
    defaultMessage: 'Initial',
  },
  initialTemplateSecondary: {
    id: 'deprecated.queries.QueryAuditsMessageTemplate.initialTemplateSecondary',
    defaultMessage: 'Luma Support',
  },
});

export interface QueryAuditsMessageTemplateProps
  extends WithStyles<typeof styles>,
    React.HTMLAttributes<HTMLDivElement> {
  messageTemplateId: string;
  onClose: () => void;
}

const QueryAuditsMessageTemplate: React.FC<QueryAuditsMessageTemplateProps> = ({
  classes,
  messageTemplateId,
  onClose,
  ...rest
}) => {
  const options = useQueryOptions('MessageTemplate');

  const data = useLazyLoadQuery<QueryAuditsMessageTemplateQuery>(
    graphql`
      query QueryAuditsMessageTemplateQuery($id: MongoID!) {
        viewer {
          ...DialogMessageTemplateAuditRestore_Viewer
        }
        messageTemplateById(_id: $id) {
          template
          configTemplate
          ...TypographyMessageTemplateKey_MessageTemplate
          ...DialogMessageTemplateAuditRestore_MessageTemplate
        }
      }
    `,
    {
      id: messageTemplateId,
      // We need to pass both since `Audit.modelId` is String and `MessageTemplate._id` is MongoID!
      modelId: messageTemplateId,
    },
    options,
  );

  const { formatMessage } = useIntl();
  const [auditSelected, setAuditSelected] = useState<Audit>();
  const [open, setOpen] = useState(false);

  const initialAudit = useMemo<Audit>(
    () => ({
      _id: 'fake_initial',
      namespace: 'fake',
      function: 'fake',
      modelField: 'fake',
      user: 'fake',
      modelId: 'fake',
      by: 'fake',
      operation: 'fake',
      description: 'fake',
      service: 'fake',
      old: '',
      new: `${data.messageTemplateById?.configTemplate || ''}`,
      athenaAuditLog: false,
      timestamp: 'fake',
      result: 'fake',
      collectionName: 'fake',
    }),
    [data.messageTemplateById],
  );

  if (!data.messageTemplateById) {
    return null;
  }

  const currentValue = data.messageTemplateById.template || '';
  return (
    <>
      <div {...rest}>
        <ToolbarNavigation disableGutters onBack={onClose}>
          <Typography variant="h6" color="primary">
            {formatMessage(messages.title, {
              value: (
                <TypographyMessageTemplateKey
                  key="key"
                  fragmentRef={data.messageTemplateById}
                  variant="inherit"
                  color="inherit"
                  inline
                />
              ),
            })}
          </Typography>
          <div className={classes.gap} />
          <Button
            variant="contained"
            color="primary"
            disabled={!auditSelected || auditSelected.old === currentValue}
            onClick={() => setOpen(true)}
          >
            {formatMessage(messages.actionRestoreVersion)}
          </Button>
        </ToolbarNavigation>
        <div className={classes.body}>
          <Paper className={classes.content} elevation={0} square>
            <ReactDiffViewer
              oldValue={currentValue}
              newValue={auditSelected ? (auditSelected.new as string) : currentValue}
              splitView={false}
              showDiffOnly={false}
            />
          </Paper>
          <Paper className={classes.listWrapper} elevation={0} square>
            <ListAuditsMessageTemplate
              modelId={messageTemplateId}
              idsSelected={auditSelected ? [auditSelected._id] : []}
              onNodeSelected={(event, node) => setAuditSelected(node)}
              disablePadding
            >
              {/* WORKAROUND additional audit for initial value coming from config file */}
              <ListItem
                selected={auditSelected && auditSelected._id === initialAudit._id}
                button
                onClick={() => setAuditSelected(initialAudit)}
              >
                <ListItemText
                  primary={formatMessage(messages.initialTemplatePrimary)}
                  secondary={formatMessage(messages.initialTemplateSecondary)}
                />
              </ListItem>
            </ListAuditsMessageTemplate>
          </Paper>
        </div>
      </div>
      {auditSelected && (
        <DialogMessageTemplateAuditRestore
          fragmentRef={data.messageTemplateById}
          fragmentRefViewer={data.viewer!}
          audit={auditSelected}
          open={open}
          onClose={() => {
            setOpen(false);
            setAuditSelected(undefined);
          }}
        />
      )}
    </>
  );
};

export default withStyles(styles)((props: QueryAuditsMessageTemplateProps) => (
  <ErrorBoundary>
    <Suspense fallback={null}>
      <QueryAuditsMessageTemplate {...props} />
    </Suspense>
  </ErrorBoundary>
));
